// Screen 4 — Invitation accept (from an open link /i/:token).
// Public preview → sign in with Google or Microsoft → accept in the guarded order
// (session is already stashed via ?next; accept → assert connection_id non-null;
// null = "finish setup", never a false success) → land on the overlap with A.
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth } from '../lib/supabase';
import { getInvitationPreview, acceptInvitation } from '../services/invitations';
import { listConnections } from '../services/network';
import { capture } from '../lib/analytics';
import { Screen, Card, Eyebrow, Title, PrivacyLine, Button, Spinner } from '../components/ui';
import EmailSignIn from '../components/EmailSignIn';

export default function Invite() {
  const { token } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [showEmail, setShowEmail] = useState(false);

  useEffect(() => {
    capture('link_opened');
    getInvitationPreview(token)
      .then(setPreview)
      .catch((err) => setError(err.status === 404
        ? 'This link isn\'t valid anymore.'
        : (err.message || 'This link can\'t be opened.')))
      .finally(() => setLoadingPreview(false));
  }, [token]);

  const inviterName = preview?.inviter?.first_name || 'Someone';

  const signInToAccept = async (provider) => {
    capture('signin_started', { provider });
    // Carry the invite target across the OAuth + calendar-connect hops.
    const next = encodeURIComponent(`/i/${token}`);
    await auth.signInWith(provider, {
      redirectTo: `${window.location.origin}/auth/callback?next=${next}`,
    });
  };

  const accept = async () => {
    setAccepting(true); setError(null);
    try {
      const res = await acceptInvitation(token);
      // Assert the connection actually landed — never a false success.
      if (!res?.connection_id) {
        setError('Almost there — finish setting up your account, then reopen this link.');
        return;
      }
      capture('connection_active');
      // Route to the overlap with A: resolve A's user id from the new connection.
      const conns = await listConnections().catch(() => []);
      const match = conns.find((c) => c.id === res.connection_id);
      navigate(match?.principal?.id ? `/with/${match.principal.id}` : '/home', { replace: true });
    } catch (err) {
      if (err.status === 409 && err.detail === undefined && /own link/i.test(err.message || '')) {
        setError('This is your own link — share it with someone else.');
      } else if (err.status === 400) {
        setError('This link has already been used.');
      } else {
        setError(err.message || 'Couldn\'t accept. Try again.');
      }
    } finally {
      setAccepting(false);
    }
  };

  if (loadingPreview || loading) return <Screen><Spinner /></Screen>;

  if (error && !preview) {
    return (
      <Screen>
        <Card>
          <Title className="text-2xl">Link unavailable</Title>
          <p className="mt-4 font-sans text-base text-ink/70">{error}</p>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen>
      <Card>
        <Eyebrow>when.vennio.app</Eyebrow>
        <Title className="mt-4 text-3xl">
          {inviterName} wants to see when you're both free.
        </Title>
        <p className="mt-4 font-sans text-base text-ink/70">
          Connect your calendar and you'll both see the free time you have in
          common. Pick one, and it lands on both calendars.
        </p>
        <PrivacyLine />

        <div className="mt-8">
          {user ? (
            <Button onClick={accept} disabled={accepting}>
              {accepting ? 'Connecting…' : 'Accept & connect'}
            </Button>
          ) : (
            showEmail ? (
              <>
                <button
                  onClick={() => setShowEmail(false)}
                  className="mb-4 font-mono text-xs text-ink/40 hover:text-ink"
                >
                  ← back
                </button>
                <EmailSignIn redirectTo={`${window.location.origin}/auth/callback?next=${encodeURIComponent(`/i/${token}`)}`} />
              </>
            ) : (
              <>
                <p className="mb-3 font-sans text-base text-ink/70">Who's your calendar with?</p>
                <div className="space-y-3">
                  <Button variant="secondary" onClick={() => signInToAccept('google')}>Google</Button>
                  <Button variant="secondary" onClick={() => signInToAccept('microsoft')}>Microsoft</Button>
                  <Button variant="secondary" onClick={() => setShowEmail(true)}>Apple</Button>
                </div>
              </>
            )
          )}
          {error && <p className="mt-3 font-mono text-xs text-red-700">{error}</p>}
        </div>
      </Card>
    </Screen>
  );
}
